import { IMG } from "./images";
import { buildLocationServiceSeo } from "./serviceSeo";

// Each location page carries distinct, factual content: local focus, pickup/dispatch
// reality, relevant services, suitable vehicles and genuinely local FAQs.
// No invented offices, local fleet bases or guaranteed instant pickup per city.

const DISPATCH_DEFAULT =
  "We are based in Satna. For this route, a vehicle is usually dispatched from our Satna base or arranged at a convenient point along the way. Share your exact pickup location and time when you enquire so we can confirm the arrangement and give you an accurate quote.";

const LOCATION_DATA = [
  {
    slug: "/taxi-service-in-maihar",
    city: "Maihar",
    priority: "primary",
    heroImage: IMG.templeMP,
    heroAlt: "Hilltop temple in Madhya Pradesh, a popular pilgrimage visit near Maihar",
    title: "Maihar Taxi Services & Car Hire | Maa Sharda Temple Trips",
    description:
      "Plan Maihar taxi services, car hire and cab booking for Maa Sharda Devi temple visits and Satna transfers. Confirm waiting time and Tempo Traveller hire needs.",
    h1: "Maihar Taxi Services for Maa Sharda Devi Temple Visits",
    intro:
      "Maihar is best known for the Maa Sharda Devi temple, and most of our Maihar enquiries are for temple darshan trips and Satna–Maihar transfers. We arrange cars for individual pilgrims as well as Tempo Travellers for family and group darshan.",
    intro2:
      "For car hire for a Maihar visit, share your group size, pickup point and preferred travel date. Tell us whether the vehicle should wait during darshan and whether you plan to return the same day. Include these requirements in your cab booking enquiry so waiting and return arrangements can be confirmed.",
    highlights: [
      "Satna to Maihar one-way and round-trip transfers",
      "Group darshan travel by Tempo Traveller",
      "Day-trip planning with waiting time for temple visit",
      "Pickup from Maihar railway station or your address on request",
    ],
    serviceTypes: ["Temple / pilgrimage travel", "Local & round trip", "Group travel", "Station transfer"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Common pickup points include Maihar railway station, the temple ropeway area and your home or hotel in Maihar or Satna.",
    dispatch: DISPATCH_DEFAULT,
    connections: [
      { label: "Satna (base)", to: "/" },
      { label: "Rewa", to: "/taxi-service-in-rewa" },
      { label: "Katni", to: "/taxi-service-in-katni" },
    ],
    faqs: [
      {
        q: "Can you arrange a same-day Satna–Maihar darshan trip?",
        a: "Yes. A same-day round trip is common. Let us know your preferred pickup time and how long you expect to spend at the temple so we can include waiting time in the quote.",
      },
      {
        q: "Do you have Tempo Travellers for a group darshan?",
        a: "Yes, Tempo Travellers are available for group pilgrimage travel to Maihar. Share the group size and date and we will confirm a suitable vehicle and quote.",
      },
      {
        q: "Is the fare fixed or by the kilometre?",
        a: "Published local packages cover 120 KM / 12 hours plus fuel. Outstation rates are per KM including fuel, with a minimum of 300 KM per day and garage-to-garage billing. Extra-hour, applicable night charges, 5% GST and other listed expenses are additional. Confirm your itinerary and final quotation before booking.",
      },
    ],
  },
  {
    slug: "/taxi-service-in-rewa",
    city: "Rewa",
    priority: "primary",
    heroImage: IMG.outstation,
    heroAlt: "Highway road trip view representing Rewa outstation taxi journeys",
    title: "Rewa Taxi Services & Car Hire | Satna & Prayagraj Trips",
    description:
      "Arrange taxi services, car hire and cab booking in Rewa for Satna and Prayagraj journeys. Ask about Tempo Traveller hire and confirm your route and dates.",
    h1: "Rewa Taxi Services for Satna & Prayagraj Journeys",
    intro:
      "Rewa sits on the busy Satna–Rewa corridor and many of our Rewa enquiries are for outstation journeys and connections towards Mauganj, Prayagraj and Satna. We arrange sedans and SUVs for individual travel and Tempo Travellers for groups.",
    intro2:
      "For car hire in Rewa, tell us whether you need a one-way journey, a return trip or a vehicle across several days. Share the complete route and travel dates with your cab booking enquiry so pickup arrangements, vehicle availability and the journey plan can be confirmed.",
    highlights: [
      "Rewa city pickups and outstation drops",
      "Rewa–Satna and Rewa–Mauganj connections",
      "Onward travel towards Prayagraj / Allahabad",
      "Group travel by Tempo Traveller",
    ],
    serviceTypes: ["Outstation one-way & round trip", "Local travel", "Group travel", "Multi-day tours"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Pickup is commonly arranged from Rewa city, the bus stand area or your home/hotel address.",
    dispatch: DISPATCH_DEFAULT,
    connections: [
      { label: "Satna (base)", to: "/" },
      { label: "Mauganj", to: "/taxi-service-in-mauganj" },
      { label: "Prayagraj", to: "/prayagraj-taxi-service" },
    ],
    faqs: [
      {
        q: "Do you run Rewa to Prayagraj / Allahabad trips?",
        a: "Yes, Rewa to Prayagraj is a frequently requested outstation route. Tell us whether it is one-way or round trip and your travel date for a quote.",
      },
      {
        q: "Can I get a one-way drop from Rewa to Satna?",
        a: "Yes, one-way Rewa–Satna drops are available. Share your pickup point and time and we will confirm the fare.",
      },
      {
        q: "How soon should I book?",
        a: "Prebooking a day or more in advance helps us confirm the right vehicle, especially for Tempo Travellers and early-morning departures.",
      },
    ],
  },
  {
    slug: "/taxi-service-in-panna",
    city: "Panna",
    priority: "primary",
    heroImage: IMG.forestRoad,
    heroAlt: "Forest road representing travel to Panna and onward to Khajuraho",
    title: "Panna Taxi Services & Cab Booking | Khajuraho Car Hire",
    description:
      "Plan taxi services, car hire and cab booking for Panna road transfers linked with Satna and Khajuraho. Enquire about Tempo Traveller rental for your group.",
    h1: "Panna Taxi Services & Car Hire for Khajuraho Transfers",
    intro:
      "Panna lies on the route between Satna and Khajuraho, so most Panna enquiries are road transfers combined with Khajuraho sightseeing or Satna connections. We arrange cars and Tempo Travellers for these journeys.",
    intro2:
      "Planning a Khajuraho itinerary via Panna? Include every pickup, sightseeing stop, overnight stay and final drop in your car hire enquiry. Providing the full plan with your cab booking request helps us review the journey as a whole and confirm vehicle availability before you travel.",
    highlights: [
      "Panna road transfers linked with Satna and Khajuraho",
      "Sightseeing-friendly itinerary planning",
      "Comfortable vehicles for families and groups",
      "Pickup from your Panna address or hotel",
    ],
    serviceTypes: ["Road transfers", "Sightseeing travel", "Group travel", "Outstation"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Pickup is commonly arranged from Panna town or your hotel; share the exact point when enquiring.",
    dispatch: DISPATCH_DEFAULT,
    connections: [
      { label: "Khajuraho", to: "/khajuraho-taxi-service" },
      { label: "Satna (base)", to: "/" },
      { label: "Chhatarpur", to: "/taxi-service-in-chhatarpur" },
    ],
    faqs: [
      {
        q: "Can you combine Panna with a Khajuraho trip?",
        a: "Yes. A combined Panna–Khajuraho plan is common. Share your dates and interests and we will propose a route and vehicle.",
      },
      {
        q: "Do you provide safari vehicles for Panna Tiger Reserve?",
        a: "We provide road transfers to and from Panna. Safari vehicles, permits and park entry are handled by the forest department and authorised operators — please arrange those separately.",
      },
      {
        q: "Is a Tempo Traveller suitable for this route?",
        a: "Yes, for groups a Tempo Traveller works well. Tell us your group size for a suitable recommendation and quote.",
      },
    ],
  },
  {
    slug: "/taxi-service-in-mauganj",
    city: "Mauganj",
    priority: "primary",
    heroImage: IMG.outstation,
    heroAlt: "Open highway representing Mauganj pickup and drop journeys",
    title: "Mauganj Taxi Services & Car Hire | Rewa Route Cab Booking",
    description:
      "Prebook Mauganj taxi services, car hire and cab booking for Rewa and Satna connections. Send your route and dates to arrange Tempo Traveller booking for groups.",
    h1: "Prebook Mauganj Taxi Services for Rewa & Satna Connections",
    intro:
      "Mauganj enquiries are mostly pickup and drop requests and journeys towards Rewa and Satna. We arrange cars and Tempo Travellers depending on your group size and route.",
    intro2:
      "Our business is based in Satna, so journeys involving Mauganj should be discussed and booked in advance. For car hire along the Rewa corridor, share the full route, pickup point and travel dates. Include intermediate stops and return requirements in your cab booking enquiry for availability confirmation.",
    highlights: [
      "Mauganj pickup and drop requests",
      "Mauganj–Rewa and Mauganj–Satna connections",
      "Outstation and multi-day trip planning",
      "Group travel by Tempo Traveller",
    ],
    serviceTypes: ["Pickup & drop", "Outstation", "Group travel", "Connections via Rewa"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Pickup is commonly arranged from Mauganj town or your address; mention the exact point when enquiring.",
    dispatch: DISPATCH_DEFAULT,
    connections: [
      { label: "Rewa", to: "/taxi-service-in-rewa" },
      { label: "Satna (base)", to: "/" },
      { label: "Prayagraj", to: "/prayagraj-taxi-service" },
    ],
    faqs: [
      {
        q: "How is the vehicle arranged for Mauganj?",
        a: "Mauganj is served from our Satna base and via the Rewa route. Prebooking in advance lets us position a vehicle on time. Share your date and pickup point for a quote.",
      },
      {
        q: "Can you drop me from Mauganj to Rewa or Satna?",
        a: "Yes, one-way drops to Rewa and Satna are available. Confirm your pickup time and we will share the fare.",
      },
      {
        q: "Do you handle early-morning pickups?",
        a: "Yes, with advance booking. Let us know the time so we can confirm the arrangement.",
      },
    ],
  },
  {
    slug: "/taxi-service-in-katni",
    city: "Katni",
    priority: "primary",
    heroImage: IMG.outstation,
    heroAlt: "Highway representing Katni onward travel to Bandhavgarh, Umaria and Jabalpur",
    title: "Katni Taxi Services & Cab Booking | Bandhavgarh Transfers",
    description:
      "Arrange Katni taxi services, car hire and cab booking for station pickups towards Bandhavgarh, Umaria and Jabalpur. Discuss Tempo Traveller rental for your group.",
    h1: "Katni Cab Booking for Station & Bandhavgarh Transfers",
    intro:
      "Katni is a major railway junction, so many enquiries are station pickups with onward travel towards Bandhavgarh, Umaria and Jabalpur. We arrange cars and Tempo Travellers for these connections.",
    intro2:
      "A Katni station pickup needs the exact station name, train number and arrival time, alongside the passenger count and onward destination. Include these details in your cab booking enquiry. For car hire covering additional stops or a return journey, share the full itinerary before confirming the vehicle.",
    highlights: [
      "Katni Junction station pickups",
      "Onward travel to Bandhavgarh, Umaria and Jabalpur",
      "Group travel by Tempo Traveller",
      "Outstation and multi-day planning",
    ],
    serviceTypes: ["Station transfer", "Outstation", "Group travel", "Onward connections"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Please specify the exact station (Katni Junction / Katni Murwara / Katni South) or address and your train time for a smooth pickup.",
    dispatch: DISPATCH_DEFAULT,
    connections: [
      { label: "Bandhavgarh", to: "/bandhavgarh-taxi-service" },
      { label: "Umaria", to: "/taxi-service-in-umaria" },
      { label: "Jabalpur", to: "/taxi-service-in-jabalpur" },
    ],
    faqs: [
      {
        q: "Which Katni station should I mention for pickup?",
        a: "Katni has more than one station. Please tell us whether it is Katni Junction, Katni Murwara or Katni South, along with your train arrival time, so the driver reaches the right point.",
      },
      {
        q: "Can you take me from Katni to Bandhavgarh?",
        a: "Yes, Katni to Bandhavgarh road transfers are a common request. Share your date and group size for a quote.",
      },
      {
        q: "Do you offer Tempo Travellers from Katni?",
        a: "Yes, for groups. Prebook so we can confirm a suitable Tempo Traveller for your date and route.",
      },
    ],
  },
  {
    slug: "/chitrakoot-taxi-service",
    city: "Chitrakoot",
    displayName: "Chitrakoot",
    priority: "primary",
    heroImage: IMG.temple,
    heroAlt: "Pilgrimage temple scene representing Chitrakoot and Chitrakoot Dham travel",
    title: "Chitrakoot Taxi Services & Car Hire | Darshan Cab Booking",
    description:
      "Plan Chitrakoot taxi services, car hire and cab booking for Ramghat, Kamadgiri and Karwi connections. Discuss Tempo Traveller booking for your pilgrimage group.",
    h1: "Chitrakoot Taxi Services for Ramghat & Kamadgiri Visits",
    intro:
      "Chitrakoot — also known as Chitrakoot Dham — is an important pilgrimage destination spanning the Madhya Pradesh and Uttar Pradesh border. Most enquiries here are darshan and sightseeing trips covering spots like Ramghat, Kamadgiri, Hanuman Dhara and Gupt Godavari.",
    intro2:
      "Plan car hire around your family or group pilgrimage itinerary, including the stops you want to visit and your return requirements. If arriving by train through Karwi, mention the station pickup in your cab booking enquiry and provide the train details, arrival time and passenger count.",
    highlights: [
      "Chitrakoot Dham darshan and local sightseeing circuits",
      "Multi-stop temple visits in one comfortable trip",
      "Group pilgrimage travel by Tempo Traveller",
      "Connections with Karwi railway station and Satna",
    ],
    serviceTypes: ["Pilgrimage / darshan", "Local sightseeing", "Group travel", "Outstation"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Pickup is commonly arranged from Karwi station, your Chitrakoot hotel or dharamshala; mention the exact point when enquiring.",
    dispatch: DISPATCH_DEFAULT,
    connections: [
      { label: "Karwi station", to: "/karwi-taxi-service" },
      { label: "Satna (base)", to: "/" },
      { label: "Prayagraj", to: "/prayagraj-taxi-service" },
    ],
    faqs: [
      {
        q: "Can you plan a full Chitrakoot darshan circuit?",
        a: "Yes. We can plan a multi-stop circuit covering the main darshan and sightseeing points. Tell us your group size and the spots you want to cover for a suitable vehicle and quote.",
      },
      {
        q: "How do I reach Chitrakoot by train and get a taxi?",
        a: "Many visitors arrive at Chitrakoot Dham Karwi railway station. We arrange station pickups — see our Karwi page — and onward darshan travel.",
      },
      {
        q: "Do you have Tempo Travellers for pilgrimage groups?",
        a: "Yes, Tempo Travellers are well suited to group darshan. Share your group size and dates for a quote.",
      },
    ],
  },
  {
    slug: "/karwi-taxi-service",
    city: "Karwi",
    priority: "primary",
    heroImage: IMG.forestRoad,
    heroAlt: "Road scene representing Karwi town and railway station transfers",
    title: "Karwi Taxi Services & Cab Booking | Chitrakoot Station Trips",
    description:
      "Arrange Karwi taxi services, car hire and cab booking for Chitrakoot Dham Karwi station pickups and onward darshan travel. Ask about Tempo Traveller rental.",
    h1: "Karwi Cab Booking for Station Pickups & Chitrakoot",
    intro:
      "Karwi — home to Chitrakoot Dham Karwi railway station — is the main railhead many visitors use to reach Chitrakoot. Most Karwi enquiries are station pickups and drops with onward travel into Chitrakoot for darshan.",
    intro2:
      "For a station pickup in Karwi, include your train number, arrival time and exact exit or meeting point in the cab booking request. Car hire arrangements can then be discussed around your arrival. For pilgrimage plans beyond the railhead, see the Chitrakoot page and share your onward itinerary.",
    highlights: [
      "Chitrakoot Dham Karwi railway station pickup and drop",
      "Onward transfers into Chitrakoot for darshan",
      "Karwi town local trips",
      "Group travel by Tempo Traveller",
    ],
    serviceTypes: ["Station transfer", "Local travel", "Pilgrimage connection", "Group travel"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Please share your train arrival time and the station exit; pickup is arranged at Chitrakoot Dham Karwi station or your Karwi address.",
    dispatch: DISPATCH_DEFAULT,
    connections: [
      { label: "Chitrakoot", to: "/chitrakoot-taxi-service" },
      { label: "Satna (base)", to: "/" },
      { label: "Prayagraj", to: "/prayagraj-taxi-service" },
    ],
    faqs: [
      {
        q: "Will the driver meet me at Karwi station?",
        a: "Yes. Share your train arrival time and the station exit you will use so the driver can meet you promptly on arrival.",
      },
      {
        q: "How far is Karwi station from the Chitrakoot temples?",
        a: "Karwi station is a short drive from the main Chitrakoot darshan area. We arrange the station pickup and onward sightseeing in one trip.",
      },
      {
        q: "Can I book a round trip back to catch my return train?",
        a: "Yes. Tell us your return train time and we will plan the darshan and drop so you reach the station comfortably.",
      },
    ],
  },
  {
    slug: "/taxi-service-in-chhatarpur",
    city: "Chhatarpur",
    priority: "primary",
    heroImage: IMG.khajuraho2,
    heroAlt: "Temple architecture in the Chhatarpur region near Khajuraho",
    title: "Chhatarpur Taxi Services & Car Hire | Khajuraho Transfers",
    description:
      "Request taxi services, car hire and cab booking for Chhatarpur town pickups and onward Khajuraho travel. Discuss Tempo Traveller hire and your complete route.",
    h1: "Chhatarpur Taxi Services for Town Pickups & Khajuraho",
    intro:
      "Chhatarpur is a district town and road hub in the Bundelkhand region. Enquiries here are typically town pickups and drops and onward outstation travel, including the route to Khajuraho, which has very different visitor needs.",
    intro2:
      "Start your Chhatarpur car hire enquiry with the exact pickup address, travel dates and intended drop location. If your plans also include Khajuraho sightseeing, see the separate Khajuraho page and include both requirements in your cab booking request.",
    highlights: [
      "Chhatarpur town pickups and drops",
      "Onward outstation journeys",
      "Connection to the distinct Khajuraho sightseeing route",
      "Group travel by Tempo Traveller",
    ],
    serviceTypes: ["Pickup & drop", "Outstation", "Sightseeing connection", "Group travel"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Pickup is commonly arranged from Chhatarpur town or your address; mention the exact point when enquiring.",
    dispatch: DISPATCH_DEFAULT,
    connections: [
      { label: "Khajuraho", to: "/khajuraho-taxi-service" },
      { label: "Panna", to: "/taxi-service-in-panna" },
      { label: "Satna (base)", to: "/" },
    ],
    faqs: [
      {
        q: "Is Chhatarpur the same as Khajuraho?",
        a: "No. Khajuraho is a separate temple-town destination with its own visitor needs; Chhatarpur is the district town. We cover both — see the Khajuraho page for sightseeing trips.",
      },
      {
        q: "Can you drive me from Chhatarpur to Khajuraho?",
        a: "Yes, Chhatarpur–Khajuraho transfers are common. Share your date and we will quote the trip and suggest a vehicle.",
      },
      {
        q: "Do you offer round trips from Chhatarpur?",
        a: "Yes. Tell us your destination, dates and group size for a round-trip quote.",
      },
    ],
  },
  {
    slug: "/khajuraho-taxi-service",
    city: "Khajuraho",
    priority: "primary",
    heroImage: IMG.khajuraho,
    heroAlt: "Intricately carved stone temple at Khajuraho, a UNESCO World Heritage site",
    title: "Khajuraho Taxi Services & Car Hire | Temple Tour Booking",
    description:
      "Explore Khajuraho temple groups with taxi services, car hire and cab booking for sightseeing and visitor transfers. Request Tempo Traveller booking for groups.",
    h1: "Khajuraho Taxi Services for Temple Visits & Transfers",
    intro:
      "Khajuraho, famous for its UNESCO-listed temples, is a major sightseeing destination. Most enquiries are visitor transfers and local sightseeing — the Western and Eastern temple groups — plus itineraries combining Panna and Chhatarpur.",
    intro2:
      "Couples and families can enquire about car hire, while larger parties can discuss a Tempo Traveller suited to their group size. Include your sightseeing stops, length of stay and final drop in the cab booking request so the arrangements can be confirmed for your complete Khajuraho visit.",
    highlights: [
      "Airport / station transfers and hotel pickups",
      "Local temple sightseeing (Western & Eastern groups)",
      "Combined Khajuraho–Panna–Chhatarpur itineraries",
      "Group sightseeing by Tempo Traveller",
    ],
    serviceTypes: ["Sightseeing", "Transfers", "Itinerary planning", "Group travel"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Pickup is commonly arranged from Khajuraho airport, railway station or your hotel; mention the exact point when enquiring.",
    dispatch: DISPATCH_DEFAULT,
    connections: [
      { label: "Panna", to: "/taxi-service-in-panna" },
      { label: "Chhatarpur", to: "/taxi-service-in-chhatarpur" },
      { label: "Satna (base)", to: "/" },
    ],
    faqs: [
      {
        q: "Can you arrange a half-day or full-day Khajuraho sightseeing cab?",
        a: "Yes. We can plan half-day or full-day sightseeing covering the main temple groups. Share your dates and interests for an itinerary and quote.",
      },
      {
        q: "Do you pick up from Khajuraho airport or station?",
        a: "Yes, we arrange airport and railway station transfers. Share your flight or train time and we will plan the pickup.",
      },
      {
        q: "Is a Tempo Traveller good for a tour group here?",
        a: "Yes, Tempo Travellers suit sightseeing groups well. Tell us your group size and plan for a suitable vehicle and quote.",
      },
    ],
  },
  {
    slug: "/bandhavgarh-taxi-service",
    city: "Bandhavgarh",
    priority: "primary",
    heroImage: IMG.tiger,
    heroAlt: "Tiger in the forest, representing the Bandhavgarh National Park region",
    title: "Bandhavgarh Taxi Services & Car Hire | Resort Transfers",
    description:
      "Arrange Bandhavgarh taxi services, car hire and cab booking for resort road transfers from Umaria or Katni. Ask about Tempo Traveller hire; safari travel is separate.",
    h1: "Bandhavgarh Taxi Services for Resort Road Transfers",
    intro:
      "Bandhavgarh National Park is a leading tiger destination near Tala/Umaria. Our service here is road travel — comfortable transfers to and from hotels and resorts, and group/luggage-friendly vehicles for your journey to the park area.",
    intro2:
      "Car hire for Bandhavgarh covers road transfers only; it does not include safari travel inside the reserve. Share your pickup, accommodation and travel dates in the cab booking enquiry. Safari jeeps, permits and gate bookings are separate and must be arranged through the forest department or authorised operators.",
    highlights: [
      "Road transfers to Bandhavgarh hotels and resorts",
      "Station transfers from Umaria and Katni",
      "Group and luggage-friendly Tempo Travellers",
      "Comfortable long-distance approach journeys",
    ],
    serviceTypes: ["Resort road transfers", "Station transfer", "Group travel", "Outstation"],
    vehicles: ["SUV / MPV", "Sedan", "Tempo Traveller"],
    pickupInfo:
      "Pickup is commonly arranged from Umaria or Katni stations or your city of origin; mention your resort and arrival time when enquiring.",
    dispatch: DISPATCH_DEFAULT,
    connections: [
      { label: "Umaria", to: "/taxi-service-in-umaria" },
      { label: "Katni", to: "/taxi-service-in-katni" },
      { label: "Jabalpur", to: "/taxi-service-in-jabalpur" },
    ],
    faqs: [
      {
        q: "Do you provide the safari jeep inside Bandhavgarh?",
        a: "No. Safari vehicles, permits and park entry are arranged by the forest department and authorised safari operators. We provide road transfers to and from Bandhavgarh and your resort.",
      },
      {
        q: "Can you transfer me from Umaria or Katni station to my resort?",
        a: "Yes. Umaria and Katni station transfers to Bandhavgarh resorts are common. Share your train time and resort name for a quote.",
      },
      {
        q: "Is a Tempo Traveller available for a group with luggage?",
        a: "Yes, Tempo Travellers suit groups with luggage. Tell us group size and dates for a suitable vehicle and quote.",
      },
    ],
  },
  {
    slug: "/taxi-service-in-umaria",
    city: "Umaria",
    priority: "primary",
    heroImage: IMG.forestRoad,
    heroAlt: "Forest road representing Umaria town and onward Bandhavgarh transfers",
    title: "Umaria Taxi Services & Car Hire | Bandhavgarh Resort Trips",
    description:
      "Book by enquiry for Umaria taxi services, car hire and cab booking from town or station to Bandhavgarh resorts. Request Tempo Traveller booking for road transfers.",
    h1: "Umaria Taxi Services for Station & Bandhavgarh Resort Transfers",
    intro:
      "Umaria is the district town and nearest major railhead for Bandhavgarh. Most Umaria enquiries are station or town pickups with onward road transfers to Bandhavgarh resorts, which is a different need from in-park safari travel.",
    intro2:
      "For car hire involving Umaria, tell us whether the pickup is from a railway station, a resort or another address. Include train details where relevant, the resort name and your group size in the cab booking request, along with luggage needs and the final drop location.",
    highlights: [
      "Umaria railway station and town pickups",
      "Onward road transfers to Bandhavgarh resorts",
      "Group travel by Tempo Traveller",
      "Outstation and regional trips",
    ],
    serviceTypes: ["Station transfer", "Resort transfer", "Group travel", "Outstation"],
    vehicles: ["SUV / MPV", "Sedan", "Tempo Traveller"],
    pickupInfo:
      "Pickup is commonly arranged from Umaria railway station or your address; share your train time and resort for a smooth transfer.",
    dispatch: DISPATCH_DEFAULT,
    connections: [
      { label: "Bandhavgarh", to: "/bandhavgarh-taxi-service" },
      { label: "Katni", to: "/taxi-service-in-katni" },
      { label: "Shahdol", to: "/taxi-service-in-shahdol" },
    ],
    faqs: [
      {
        q: "How far is Umaria from Bandhavgarh?",
        a: "Umaria is the nearest major railhead and is a short road transfer from the Bandhavgarh (Tala) area. We arrange the station pickup and onward transfer together.",
      },
      {
        q: "Will the driver wait if my train is late?",
        a: "Share your train number and arrival time when booking. We plan for the scheduled arrival; let us know of delays so the driver can adjust.",
      },
      {
        q: "Do you offer Tempo Travellers from Umaria?",
        a: "Yes, for groups. Prebook so we can confirm a suitable Tempo Traveller for your date.",
      },
    ],
  },
  {
    slug: "/taxi-service-in-shahdol",
    city: "Shahdol",
    priority: "primary",
    heroImage: IMG.outstation,
    heroAlt: "Highway view representing Shahdol regional and outstation journeys",
    title: "Shahdol Car Hire & Tempo Traveller Rental | Umaria & Katni",
    description:
      "Plan Shahdol taxi services, car hire and cab booking for town pickups and Umaria or Katni connections. Request Tempo Traveller rental with dates and return plans.",
    h1: "Shahdol Car Hire for Umaria & Katni Connections",
    intro:
      "Shahdol enquiries are typically town pickups and regional or outstation journeys, including connections towards Umaria, Katni and the wider region. We arrange cars and Tempo Travellers to suit.",
    intro2:
      "Journeys involving Shahdol should be planned in advance. For car hire, provide the pickup address, destination and travel dates. Include any regional stops, waiting requirements and return plans in your cab booking request so the route and vehicle availability can be reviewed.",
    highlights: [
      "Shahdol town pickups and drops",
      "Regional connections towards Umaria and Katni",
      "Outstation and multi-day trips",
      "Group travel by Tempo Traveller",
    ],
    serviceTypes: ["Pickup & drop", "Outstation", "Regional travel", "Group travel"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Pickup is commonly arranged from Shahdol town, station or your address; mention the exact point when enquiring.",
    dispatch: DISPATCH_DEFAULT,
    connections: [
      { label: "Umaria", to: "/taxi-service-in-umaria" },
      { label: "Katni", to: "/taxi-service-in-katni" },
      { label: "Jabalpur", to: "/taxi-service-in-jabalpur" },
    ],
    faqs: [
      {
        q: "Do you cover outstation trips from Shahdol?",
        a: "Yes. Share your destination and travel dates and we will quote an outstation one-way or round trip and suggest a vehicle.",
      },
      {
        q: "Can I book a Shahdol to Jabalpur drop?",
        a: "Yes, Shahdol–Jabalpur drops are available. Confirm your pickup time for a quote.",
      },
      {
        q: "How far in advance should I book?",
        a: "Booking a day or more ahead helps us confirm the right vehicle, especially for Tempo Travellers.",
      },
    ],
  },
  {
    slug: "/taxi-service-in-jabalpur",
    city: "Jabalpur",
    priority: "primary",
    heroImage: IMG.forestRoad,
    heroAlt: "Road near greenery representing Jabalpur city and regional travel",
    title: "Jabalpur Taxi Services & Tempo Traveller Hire | Transfers",
    description:
      "Arrange Jabalpur taxi services, car hire and cab booking for city, station and airport transfers. Discuss Tempo Traveller hire and confirm your full route.",
    h1: "Jabalpur Taxi Services for Station, Airport & Regional Trips",
    intro:
      "Jabalpur is a major city in the region and enquiries include city travel, railway station and airport transfers and regional journeys. We arrange cars and Tempo Travellers depending on your plan.",
    intro2:
      "Share the complete route and travel dates when enquiring about car hire involving Jabalpur. Include the pickup address, intended stops and final drop in your cab booking request. Coverage and vehicle availability must be confirmed for that particular journey before you rely on the proposed arrangements.",
    highlights: [
      "City, railway station and airport transfers",
      "Regional and outstation journeys",
      "Group travel by Tempo Traveller",
      "Multi-day tour planning",
    ],
    serviceTypes: ["City travel", "Station / airport transfer", "Outstation", "Group travel"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Pickup is commonly arranged from Jabalpur railway station, airport or your address; share the exact point and time when enquiring.",
    dispatch: DISPATCH_DEFAULT,
    connections: [
      { label: "Katni", to: "/taxi-service-in-katni" },
      { label: "Bandhavgarh", to: "/bandhavgarh-taxi-service" },
      { label: "Satna (base)", to: "/" },
    ],
    faqs: [
      {
        q: "Do you confirm availability before a Jabalpur trip?",
        a: "Yes. Jabalpur coverage depends on the route and date, so we confirm availability and a quote when you enquire, before you travel.",
      },
      {
        q: "Can you pick up from Jabalpur airport or station?",
        a: "Yes, subject to confirmation. Share your flight or train time and we will plan the transfer.",
      },
      {
        q: "Are Tempo Travellers available from Jabalpur?",
        a: "Yes, for groups, subject to confirmation. Prebook with your group size and date for a quote.",
      },
    ],
  },
  {
    slug: "/taxi-service-in-indore",
    city: "Indore",
    priority: "primary",
    heroImage: IMG.heroHighway,
    heroAlt: "Highway view representing prebooked Indore city and outstation travel",
    title: "Indore Taxi Services & Tempo Traveller Booking | Prebook",
    description:
      "Prebook Indore taxi services, car hire and cab booking through our Satna-based team. Share dates and city or outstation routes for Tempo Traveller booking.",
    h1: "Prebook Indore Taxi Services for City & Outstation Travel",
    intro:
      "Indore is a priority destination for prebooked travel — city trips, outstation journeys and group travel. As we are based in Satna, Indore vehicles are arranged on a prebooked basis, so advance planning is important.",
    intro2:
      "Travel involving Indore requires advance discussion and confirmation; an enquiry does not guarantee an immediate pickup. For car hire, share your route, dates, pickup address and passenger count. Include any stops and return requirements in the cab booking request so availability can be checked.",
    highlights: [
      "Prebooked Indore city and outstation travel",
      "Group travel by Tempo Traveller",
      "Multi-day Madhya Pradesh tour planning",
      "Long-distance connections",
    ],
    serviceTypes: ["Prebooked city travel", "Outstation", "Group travel", "Multi-day tours"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Pickup is arranged on a prebooked basis from your Indore address, hotel, station or airport; share the exact point and time.",
    dispatch:
      "Indore is a prebooked-priority destination served from our Satna base. Please book in advance with your route and date so we can position a suitable vehicle and confirm an accurate quote. We do not promise instant on-demand pickup in Indore.",
    connections: [
      { label: "Bhopal", to: "/taxi-service-in-bhopal" },
      { label: "Satna (base)", to: "/" },
      { label: "Khajuraho", to: "/khajuraho-taxi-service" },
    ],
    faqs: [
      {
        q: "Is Indore an on-demand or prebooked service?",
        a: "Indore is a prebooked service. As we are based in Satna, please book in advance so we can position a suitable vehicle and confirm a quote.",
      },
      {
        q: "Can you plan a multi-day tour starting from Indore?",
        a: "Yes. Share your itinerary and dates and we will suggest a vehicle and quote for the full plan.",
      },
      {
        q: "Do you have Tempo Travellers for Indore group travel?",
        a: "Yes, on a prebooked basis. Tell us your group size and dates for a quote.",
      },
    ],
  },
  {
    slug: "/taxi-service-in-bhopal",
    city: "Bhopal",
    priority: "secondary",
    heroImage: IMG.outstation,
    heroAlt: "Highway representing Bhopal pickups and longer-distance journeys",
    title: "Bhopal Car Hire & Tempo Traveller Rental | Prebook Travel",
    description:
      "Prebook Bhopal taxi services, car hire and cab booking for long-distance journeys from Satna. Include stops and overnight plans for Tempo Traveller rental.",
    h1: "Prebook Bhopal Car Hire for Long-Distance Journeys",
    intro:
      "Bhopal is a secondary-priority destination for prebooked pickups and longer-distance journeys. As we operate from Satna, Bhopal travel is arranged in advance rather than on demand.",
    intro2:
      "For long-distance car hire involving Bhopal, provide the full route, travel dates and exact pickup and drop addresses. Include any intermediate stops or overnight plans in your cab booking enquiry. These details allow the journey and vehicle availability to be reviewed before confirmation.",
    highlights: [
      "Prebooked Bhopal pickups and drops",
      "Longer-distance outstation journeys",
      "Group travel by Tempo Traveller",
      "Multi-day tour planning",
    ],
    serviceTypes: ["Prebooked travel", "Long-distance outstation", "Group travel", "Multi-day tours"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Pickup is arranged on a prebooked basis from your Bhopal address, station or airport; share the exact point and time.",
    dispatch:
      "Bhopal is a secondary-priority, prebooked destination served from our Satna base. Please book in advance so we can arrange a suitable vehicle and confirm an accurate quote.",
    connections: [
      { label: "Indore", to: "/taxi-service-in-indore" },
      { label: "Satna (base)", to: "/" },
      { label: "Khajuraho", to: "/khajuraho-taxi-service" },
    ],
    faqs: [
      {
        q: "Do you offer on-demand cabs within Bhopal?",
        a: "Bhopal is a prebooked, secondary-priority service. We arrange it in advance from our Satna base rather than on demand.",
      },
      {
        q: "Can you drive from Satna or Khajuraho to Bhopal?",
        a: "Yes, longer-distance journeys to Bhopal are available. Share your route and dates for a quote.",
      },
      {
        q: "Are Tempo Travellers available for Bhopal trips?",
        a: "Yes, on a prebooked basis for groups. Share your group size and dates for a quote.",
      },
    ],
  },
  {
    slug: "/varanasi-taxi-service",
    city: "Varanasi",
    priority: "secondary",
    heroImage: IMG.varanasi,
    heroAlt: "Boats and ghats on the Ganga at Varanasi, a major pilgrimage destination",
    title: "Varanasi Car Hire & Tempo Traveller Booking | Pilgrimages",
    description:
      "Prebook Varanasi taxi services, car hire and cab booking for pilgrimage travel from Satna. Include Prayagraj stops and overnight plans for Tempo Traveller booking.",
    h1: "Prebook Varanasi Pilgrimage Travel with Prayagraj Stops",
    intro:
      "Varanasi is a secondary-priority destination for prebooked long-distance and pilgrimage journeys, often combined with Prayagraj and the wider circuit. As we are based in Satna, these trips are planned in advance.",
    intro2:
      "For pilgrimage travel involving Varanasi, plan car hire around your group size, travel dates and complete itinerary. Your cab booking enquiry should include pickup and drop addresses, pilgrimage stops, luggage needs and any overnight stays. Confirm availability and the proposed arrangements before finalising your journey.",
    highlights: [
      "Prebooked long-distance journeys to Varanasi",
      "Pilgrimage travel, often with Prayagraj",
      "Group travel by Tempo Traveller",
      "Multi-day tour planning",
    ],
    serviceTypes: ["Long-distance outstation", "Pilgrimage travel", "Group travel", "Multi-day tours"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Pickup is arranged on a prebooked basis from your city of origin or Varanasi address; share the exact point and time.",
    dispatch:
      "Varanasi is a secondary-priority, prebooked destination served from our Satna base. Please book in advance so we can plan the long-distance journey and confirm an accurate quote.",
    connections: [
      { label: "Prayagraj", to: "/prayagraj-taxi-service" },
      { label: "Rewa", to: "/taxi-service-in-rewa" },
      { label: "Satna (base)", to: "/" },
    ],
    faqs: [
      {
        q: "Can you combine Varanasi with Prayagraj?",
        a: "Yes. A combined Varanasi–Prayagraj pilgrimage plan is common. Share your dates and group size for an itinerary and quote.",
      },
      {
        q: "Is this an on-demand Varanasi city cab?",
        a: "No. Varanasi is a prebooked, secondary-priority service for long-distance and pilgrimage travel planned in advance from our Satna base.",
      },
      {
        q: "Do you have Tempo Travellers for a pilgrimage group?",
        a: "Yes, on a prebooked basis. Share your group size and dates for a quote.",
      },
    ],
  },
  {
    slug: "/prayagraj-taxi-service",
    city: "Prayagraj",
    displayName: "Prayagraj / Allahabad",
    priority: "secondary",
    heroImage: IMG.temple,
    heroAlt: "Pilgrimage scene representing Prayagraj (Allahabad) Sangam travel",
    title: "Prayagraj Taxi Services & Tempo Traveller Hire | Prebook",
    description:
      "Prebook Prayagraj (Allahabad) taxi services, car hire and cab booking for Sangam visits and Varanasi journeys. Ask about Tempo Traveller hire from our Satna base.",
    h1: "Prebook Prayagraj Travel for Sangam & Varanasi Visits",
    intro:
      "Prayagraj — also known as Allahabad — is a secondary-priority destination for prebooked long-distance and pilgrimage journeys, including Sangam (Triveni) visits, often combined with Varanasi. As we are based in Satna, these trips are planned in advance.",
    intro2:
      "Prayagraj and Allahabad refer to the same destination, so you only need one enquiry for your journey. For long-distance pilgrimage car hire, share your dates, group size and intended stops. Include the return plan or final drop address in your cab booking request so the full itinerary can be reviewed.",
    highlights: [
      "Prebooked long-distance journeys to Prayagraj / Allahabad",
      "Sangam and pilgrimage travel",
      "Often combined with Varanasi",
      "Group travel by Tempo Traveller",
    ],
    serviceTypes: ["Long-distance outstation", "Pilgrimage travel", "Group travel", "Multi-day tours"],
    vehicles: ["Sedan", "SUV / MPV", "Tempo Traveller"],
    pickupInfo:
      "Pickup is arranged on a prebooked basis from your city of origin or Prayagraj (Allahabad) address; share the exact point and time.",
    dispatch:
      "Prayagraj (Allahabad) is a secondary-priority, prebooked destination served from our Satna base. Please book in advance so we can plan the long-distance journey and confirm an accurate quote.",
    connections: [
      { label: "Varanasi", to: "/varanasi-taxi-service" },
      { label: "Rewa", to: "/taxi-service-in-rewa" },
      { label: "Satna (base)", to: "/" },
    ],
    faqs: [
      {
        q: "Is Prayagraj the same as Allahabad?",
        a: "Yes. Prayagraj is the current name for the city formerly called Allahabad. This page covers both names.",
      },
      {
        q: "Can you plan a Sangam darshan trip?",
        a: "Yes. We arrange prebooked pilgrimage travel including Sangam (Triveni) visits. Share your dates and group size for a plan and quote.",
      },
      {
        q: "Do you combine Prayagraj with Varanasi?",
        a: "Yes, a combined Prayagraj–Varanasi circuit is common. Tell us your dates and we will propose an itinerary and quote.",
      },
    ],
  },
{
  "slug": "/chitrakoot-dham-taxi-service",
  "city": "Chitrakoot Dham",
  "priority": "primary",
  "heroImage": IMG.outstation,
  "heroAlt": "Representative road-travel scene for prebooked Chitrakoot Dham journeys",
  "title": "Chitrakoot Dham Car Hire & Taxi Services | Group Darshan",
  "description": "Enquire about Chitrakoot Dham taxi services, car hire and cab booking for group darshan, hotel pickups and station drops. Plan Tempo Traveller hire in advance.",
  "h1": "Chitrakoot Dham Car Hire for Group Darshan & Station Drops",
  "intro": "Plan a Chitrakoot Dham pilgrimage with a car for your family or a Tempo Traveller for your group. Share the darshan stops you want to cover and the time available so we can quote a suitable itinerary.",
  "intro2": "For group darshan plans, share your passenger count, intended stops and return arrangements when discussing car hire. Include any pickup at Chitrakoot Dham Karwi station in the cab booking request. For broader Chitrakoot travel, see the connected Chitrakoot page and send one complete itinerary.",
  "highlights": [
    "Family and group darshan travel",
    "Multi-stop pilgrimage itineraries",
    "Hotel or dharamshala pickup by arrangement",
    "Return journey and station-drop planning"
  ],
  "serviceTypes": [
    "Prebooked pickup & drop",
    "Outstation",
    "Group travel",
    "Itinerary planning"
  ],
  "vehicles": [
    "Sedan",
    "SUV / MPV",
    "Tempo Traveller"
  ],
  "pickupInfo": "Tell us whether pickup is from Chitrakoot Dham Karwi station, a hotel or dharamshala, or an address in the darshan area. Include your return pickup point if it differs.",
  "dispatch": "We are based in Satna. Please share your route and date in advance so we can confirm vehicle availability, pickup arrangements and the complete quotation.",
  "connections": [
    {
      "label": "Chitrakoot",
      "to": "/chitrakoot-taxi-service"
    },
    {
      "label": "Karwi station",
      "to": "/karwi-taxi-service"
    },
    {
      "label": "Satna (base)",
      "to": "/"
    }
  ],
  "faqs": [
    {
      "q": "Can you include several darshan stops in one booking?",
      "a": "Yes, share the stops, date and group size. We will confirm the itinerary, waiting time, vehicle and fare before the trip."
    },
    {
      "q": "Which vehicles can I request for Chitrakoot Dham?",
      "a": "Dzire, Etios, Brezza, Ertiga, Bolero, Bolero Neo, Scorpio, Cruiser, Innova Crysta and Kia Carens can be requested. Published Traveller options are 13 (12+1), 17 (16+1), 20 (19+1), 21 (20+1) and 26 (25+1) seats including the driver, subject to availability. A 9-seater option requires a separate capacity and fare confirmation."
    },
    {
      "q": "How do I confirm my Chitrakoot Dham booking?",
      "a": "Send your route, dates, pickup time, passengers and luggage details. An enquiry alone is not a confirmed booking; we confirm the vehicle, fare and terms directly with you."
    }
  ]
},
{
  "slug": "/taxi-service-in-manikpur",
  "city": "Manikpur",
  "priority": "primary",
  "heroImage": IMG.outstation,
  "heroAlt": "Representative road-travel scene for prebooked Manikpur journeys",
  "title": "Manikpur Taxi Services & Cab Booking | Station Car Hire",
  "description": "Request Manikpur taxi services, car hire and cab booking for town or railway station pickups. Share train details and luggage needs for Tempo Traveller rental.",
  "h1": "Manikpur Taxi Services for Town & Railway Station Pickups",
  "intro": "Book a taxi or Tempo Traveller for Manikpur town or railway station pickup and onward travel. Specify the exact pickup point so a town-address booking is not confused with a station arrival.",
  "intro2": "For car hire from Manikpur station, share your train details, arrival time and luggage requirements. For a pickup in town, provide the address and a clear landmark. State whether you need a one-way journey or return travel in your cab booking enquiry so the arrangements can be confirmed.",
  "highlights": [
    "Manikpur town pickups",
    "Railway station pickup and drop",
    "Prebooked onward and return journeys",
    "Traveller bookings for groups with luggage"
  ],
  "serviceTypes": [
    "Prebooked pickup & drop",
    "Outstation",
    "Group travel",
    "Itinerary planning"
  ],
  "vehicles": [
    "Sedan",
    "SUV / MPV",
    "Tempo Traveller"
  ],
  "pickupInfo": "Specify Manikpur railway station or your Manikpur town address. For trains, include the date, arrival time and a contact number for coordination.",
  "dispatch": "We are based in Satna. Please share your route and date in advance so we can confirm vehicle availability, pickup arrangements and the complete quotation.",
  "connections": [
    {
      "label": "Karwi",
      "to": "/karwi-taxi-service"
    },
    {
      "label": "Chitrakoot",
      "to": "/chitrakoot-taxi-service"
    },
    {
      "label": "Satna (base)",
      "to": "/"
    }
  ],
  "faqs": [
    {
      "q": "Should I mention Manikpur town or the railway station?",
      "a": "Yes. Please specify which pickup you need. For the station, share train details; for a town pickup, share the full address or landmark."
    },
    {
      "q": "Which vehicles can I request for Manikpur?",
      "a": "Dzire, Etios, Brezza, Ertiga, Bolero, Bolero Neo, Scorpio, Cruiser, Innova Crysta and Kia Carens can be requested. Published Traveller options are 13 (12+1), 17 (16+1), 20 (19+1), 21 (20+1) and 26 (25+1) seats including the driver, subject to availability. A 9-seater option requires a separate capacity and fare confirmation."
    },
    {
      "q": "How do I confirm my Manikpur booking?",
      "a": "Send your route, dates, pickup time, passengers and luggage details. An enquiry alone is not a confirmed booking; we confirm the vehicle, fare and terms directly with you."
    }
  ]
},
{
  "slug": "/taxi-service-in-sagar",
  "city": "Sagar",
  "priority": "secondary",
  "heroImage": IMG.outstation,
  "heroAlt": "Representative road-travel scene for prebooked Sagar journeys",
  "title": "Prebook Sagar Taxi Services & Tempo Traveller Hire | Damoh",
  "description": "Prebook Sagar taxi services, car hire and cab booking from our Satna base. Include Damoh stops and return plans when requesting Tempo Traveller hire.",
  "h1": "Prebook Sagar Taxi Services for Damoh & Return Trips",
  "intro": "Request a prebooked taxi or Tempo Traveller for Sagar. Tell us whether you need a one-way drop, a return journey or a vehicle for several days so we can plan the booking from our Satna base.",
  "intro2": "For car hire involving Sagar, include any linked stops in Damoh in the same enquiry. Share the travel dates, stop order and final destination in your cab booking request. Mention expected waiting and any overnight requirement so the complete journey can be discussed before confirmation.",
  "highlights": [
    "Prebooked Sagar pickups and drops",
    "Sagar and Damoh itinerary enquiries",
    "One-way and return-journey planning",
    "Group and multi-day travel"
  ],
  "serviceTypes": [
    "Prebooked pickup & drop",
    "Outstation",
    "Group travel",
    "Itinerary planning"
  ],
  "vehicles": [
    "Sedan",
    "SUV / MPV",
    "Tempo Traveller"
  ],
  "pickupInfo": "Share your exact Sagar address, hotel or station pickup point and the required reporting time.",
  "dispatch": "We are based in Satna. Please share your route and date in advance so we can confirm vehicle availability, pickup arrangements and the complete quotation.",
  "connections": [
    {
      "label": "Damoh",
      "to": "/taxi-service-in-damoh"
    },
    {
      "label": "Bhopal",
      "to": "/taxi-service-in-bhopal"
    },
    {
      "label": "Satna (base)",
      "to": "/"
    }
  ],
  "faqs": [
    {
      "q": "Can I request a Sagar to Damoh one-way drop?",
      "a": "Yes. Share the date, pickup address, drop address and passenger count. We will confirm a suitable vehicle and quote."
    },
    {
      "q": "Which vehicles can I request for Sagar?",
      "a": "Dzire, Etios, Brezza, Ertiga, Bolero, Bolero Neo, Scorpio, Cruiser, Innova Crysta and Kia Carens can be requested. Published Traveller options are 13 (12+1), 17 (16+1), 20 (19+1), 21 (20+1) and 26 (25+1) seats including the driver, subject to availability. A 9-seater option requires a separate capacity and fare confirmation."
    },
    {
      "q": "How do I confirm my Sagar booking?",
      "a": "Send your route, dates, pickup time, passengers and luggage details. An enquiry alone is not a confirmed booking; we confirm the vehicle, fare and terms directly with you."
    }
  ]
},
{
  "slug": "/taxi-service-in-damoh",
  "city": "Damoh",
  "priority": "secondary",
  "heroImage": IMG.outstation,
  "heroAlt": "Representative road-travel scene for prebooked Damoh journeys",
  "title": "Damoh Cab Booking & Tempo Traveller Rental | Prebook Trips",
  "description": "Prebook Damoh taxi services, car hire and cab booking for trips with Sagar or Jabalpur stops. Enquire about Tempo Traveller rental for one-way or return travel.",
  "h1": "Prebook Damoh Car Hire with Sagar or Jabalpur Stops",
  "intro": "Plan a prebooked Damoh taxi journey for a one-way drop, a round trip or group travel. Cars and Tempo Travellers can be requested according to your passenger count, luggage and travel dates.",
  "intro2": "If your Damoh journey includes Sagar or Jabalpur, list the stops in travel order. Your car hire enquiry should cover pickup and drop addresses, waiting requirements and the return plan. Include the travel dates and these details together in your cab booking request.",
  "highlights": [
    "Damoh one-way drop enquiries",
    "Round trips with confirmed waiting time",
    "Sagar and Jabalpur itinerary planning",
    "Traveller enquiries for group journeys"
  ],
  "serviceTypes": [
    "Prebooked pickup & drop",
    "Outstation",
    "Group travel",
    "Itinerary planning"
  ],
  "vehicles": [
    "Sedan",
    "SUV / MPV",
    "Tempo Traveller"
  ],
  "pickupInfo": "Provide your Damoh pickup address or station details, reporting time and final drop location.",
  "dispatch": "We are based in Satna. Please share your route and date in advance so we can confirm vehicle availability, pickup arrangements and the complete quotation.",
  "connections": [
    {
      "label": "Sagar",
      "to": "/taxi-service-in-sagar"
    },
    {
      "label": "Jabalpur",
      "to": "/taxi-service-in-jabalpur"
    },
    {
      "label": "Satna (base)",
      "to": "/"
    }
  ],
  "faqs": [
    {
      "q": "Can I book a vehicle for both the outward and return journey?",
      "a": "Yes. Include both dates and any waiting time in your Damoh enquiry so the complete arrangement can be quoted."
    },
    {
      "q": "Which vehicles can I request for Damoh?",
      "a": "Dzire, Etios, Brezza, Ertiga, Bolero, Bolero Neo, Scorpio, Cruiser, Innova Crysta and Kia Carens can be requested. Published Traveller options are 13 (12+1), 17 (16+1), 20 (19+1), 21 (20+1) and 26 (25+1) seats including the driver, subject to availability. A 9-seater option requires a separate capacity and fare confirmation."
    },
    {
      "q": "How do I confirm my Damoh booking?",
      "a": "Send your route, dates, pickup time, passengers and luggage details. An enquiry alone is not a confirmed booking; we confirm the vehicle, fare and terms directly with you."
    }
  ]
},
{
  "slug": "/taxi-service-in-gwalior",
  "city": "Gwalior",
  "priority": "secondary",
  "heroImage": IMG.outstation,
  "heroAlt": "Representative road-travel scene for prebooked Gwalior journeys",
  "title": "Gwalior Cab Booking & Tempo Traveller Rental | Prebook",
  "description": "Request Gwalior taxi services, car hire and cab booking for prebooked transfers or multi-day trips. Confirm planned stops and Tempo Traveller rental needs.",
  "h1": "Prebook Gwalior Transfers & Multi-Day Car Hire",
  "intro": "Request a prebooked car or Tempo Traveller for travel to or from Gwalior. We plan these journeys from our Satna base and confirm the vehicle and reporting schedule against your itinerary.",
  "intro2": "For multi-day car hire involving Gwalior, share each travel date, overnight stop and the final drop location. For a one-way transfer, provide the exact pickup and destination addresses. Make the intended journey clear in your cab booking enquiry so the arrangements can be confirmed.",
  "highlights": [
    "Prebooked long-distance travel",
    "One-way and round-trip enquiries",
    "Multi-day itineraries with planned stops",
    "Group bookings with luggage details"
  ],
  "serviceTypes": [
    "Prebooked pickup & drop",
    "Outstation",
    "Group travel",
    "Itinerary planning"
  ],
  "vehicles": [
    "Sedan",
    "SUV / MPV",
    "Tempo Traveller"
  ],
  "pickupInfo": "Share the Gwalior address, hotel or station where you want the vehicle to report, together with the date and time.",
  "dispatch": "We are based in Satna. Please share your route and date in advance so we can confirm vehicle availability, pickup arrangements and the complete quotation.",
  "connections": [
    {
      "label": "Agra",
      "to": "/taxi-service-in-agra"
    },
    {
      "label": "Bhopal",
      "to": "/taxi-service-in-bhopal"
    },
    {
      "label": "Satna (base)",
      "to": "/"
    }
  ],
  "faqs": [
    {
      "q": "Can a Gwalior trip include an Agra stop?",
      "a": "You can request both destinations in one itinerary. Share the dates and stops, and we will confirm availability and a quote for the full journey."
    },
    {
      "q": "Which vehicles can I request for Gwalior?",
      "a": "Dzire, Etios, Brezza, Ertiga, Bolero, Bolero Neo, Scorpio, Cruiser, Innova Crysta and Kia Carens can be requested. Published Traveller options are 13 (12+1), 17 (16+1), 20 (19+1), 21 (20+1) and 26 (25+1) seats including the driver, subject to availability. A 9-seater option requires a separate capacity and fare confirmation."
    },
    {
      "q": "How do I confirm my Gwalior booking?",
      "a": "Send your route, dates, pickup time, passengers and luggage details. An enquiry alone is not a confirmed booking; we confirm the vehicle, fare and terms directly with you."
    }
  ]
},
{
  "slug": "/taxi-service-in-lucknow",
  "city": "Lucknow",
  "priority": "secondary",
  "heroImage": IMG.outstation,
  "heroAlt": "Representative road-travel scene for prebooked Lucknow journeys",
  "title": "Lucknow Taxi Services & Tempo Traveller Rental | Prebook",
  "description": "Prebook Lucknow taxi services, car hire and cab booking for station or airport transfers. Include Kanpur or Prayagraj stops in your Tempo Traveller rental enquiry.",
  "h1": "Prebook Lucknow Station & Airport Taxi Transfers",
  "intro": "Enquire about prebooked taxi and Tempo Traveller travel for Lucknow. Choose a car for a smaller party or request a Traveller for a group, with availability confirmed for your travel date.",
  "intro2": "For car hire involving Lucknow, provide the exact pickup address and any train or flight timings relevant to your journey. If Kanpur or Prayagraj is also part of the plan, include those stops in the same cab booking enquiry, together with travel dates and your final destination.",
  "highlights": [
    "Advance taxi and Traveller bookings",
    "Station and airport transfer enquiries",
    "Kanpur and Prayagraj itinerary planning",
    "Family, group and multi-day travel"
  ],
  "serviceTypes": [
    "Prebooked pickup & drop",
    "Outstation",
    "Group travel",
    "Itinerary planning"
  ],
  "vehicles": [
    "Sedan",
    "SUV / MPV",
    "Tempo Traveller"
  ],
  "pickupInfo": "Specify the Lucknow address, station or airport terminal and the required reporting time. Share any arrival details needed to coordinate the pickup.",
  "dispatch": "We are based in Satna. Please share your route and date in advance so we can confirm vehicle availability, pickup arrangements and the complete quotation.",
  "connections": [
    {
      "label": "Kanpur",
      "to": "/taxi-service-in-kanpur"
    },
    {
      "label": "Prayagraj / Allahabad",
      "to": "/prayagraj-taxi-service"
    },
    {
      "label": "Satna (base)",
      "to": "/"
    }
  ],
  "faqs": [
    {
      "q": "Can I request a timed Lucknow airport or station transfer?",
      "a": "Yes. Send the exact terminal or station, arrival or departure time and travel date. The pickup arrangement and fare are confirmed before booking."
    },
    {
      "q": "Which vehicles can I request for Lucknow?",
      "a": "Dzire, Etios, Brezza, Ertiga, Bolero, Bolero Neo, Scorpio, Cruiser, Innova Crysta and Kia Carens can be requested. Published Traveller options are 13 (12+1), 17 (16+1), 20 (19+1), 21 (20+1) and 26 (25+1) seats including the driver, subject to availability. A 9-seater option requires a separate capacity and fare confirmation."
    },
    {
      "q": "How do I confirm my Lucknow booking?",
      "a": "Send your route, dates, pickup time, passengers and luggage details. An enquiry alone is not a confirmed booking; we confirm the vehicle, fare and terms directly with you."
    }
  ]
},
{
  "slug": "/taxi-service-in-kanpur",
  "city": "Kanpur",
  "priority": "secondary",
  "heroImage": IMG.outstation,
  "heroAlt": "Representative road-travel scene for prebooked Kanpur journeys",
  "title": "Kanpur Cab Booking & Tempo Traveller Hire | Prebook Trips",
  "description": "Request prebooked Kanpur taxi services, car hire and cab booking for one-way or return journeys. Discuss Tempo Traveller hire with Lucknow or Prayagraj stops.",
  "h1": "Prebook Kanpur Car Hire for Lucknow & Prayagraj Links",
  "intro": "Book by enquiry for a prearranged Kanpur taxi or Tempo Traveller journey. Share your route, passenger count and dates for a one-way drop, round trip or multi-day plan.",
  "intro2": "When arranging car hire involving Kanpur, list any Lucknow or Prayagraj stops in travel order and provide the required reporting times. Include dates, pickup and drop addresses, and the return plan in your cab booking enquiry so the complete schedule can be reviewed before confirmation.",
  "highlights": [
    "Prearranged Kanpur pickup and drop",
    "One-way and return-trip quotations",
    "Lucknow and Prayagraj connections",
    "Traveller enquiries for larger groups"
  ],
  "serviceTypes": [
    "Prebooked pickup & drop",
    "Outstation",
    "Group travel",
    "Itinerary planning"
  ],
  "vehicles": [
    "Sedan",
    "SUV / MPV",
    "Tempo Traveller"
  ],
  "pickupInfo": "Provide the exact Kanpur address or station pickup point, travel date and reporting time.",
  "dispatch": "We are based in Satna. Please share your route and date in advance so we can confirm vehicle availability, pickup arrangements and the complete quotation.",
  "connections": [
    {
      "label": "Lucknow",
      "to": "/taxi-service-in-lucknow"
    },
    {
      "label": "Prayagraj / Allahabad",
      "to": "/prayagraj-taxi-service"
    },
    {
      "label": "Satna (base)",
      "to": "/"
    }
  ],
  "faqs": [
    {
      "q": "Can the quotation cover Kanpur and Lucknow together?",
      "a": "Yes. List both destinations, the order of travel and your dates. We will confirm a vehicle and fare for the complete itinerary."
    },
    {
      "q": "Which vehicles can I request for Kanpur?",
      "a": "Dzire, Etios, Brezza, Ertiga, Bolero, Bolero Neo, Scorpio, Cruiser, Innova Crysta and Kia Carens can be requested. Published Traveller options are 13 (12+1), 17 (16+1), 20 (19+1), 21 (20+1) and 26 (25+1) seats including the driver, subject to availability. A 9-seater option requires a separate capacity and fare confirmation."
    },
    {
      "q": "How do I confirm my Kanpur booking?",
      "a": "Send your route, dates, pickup time, passengers and luggage details. An enquiry alone is not a confirmed booking; we confirm the vehicle, fare and terms directly with you."
    }
  ]
},
{
  "slug": "/taxi-service-in-agra",
  "city": "Agra",
  "priority": "secondary",
  "heroImage": IMG.outstation,
  "heroAlt": "Representative road-travel scene for prebooked Agra journeys",
  "title": "Agra Car Hire & Tempo Traveller Rental | Prebook Tours",
  "description": "Prebook Agra taxi services, car hire and cab booking for tours and hotel or station transfers. Include Gwalior stops and waiting in your Tempo Traveller rental plan.",
  "h1": "Prebook Agra Tours, Transfers & Gwalior Journeys",
  "intro": "Plan a prebooked Agra trip with a car or Tempo Traveller. Send your dates, sightseeing or transfer requirements and group size so we can quote the vehicle journey from our Satna base.",
  "intro2": "For an Agra tour, include planned stops and the daily hours you need the vehicle when discussing car hire. For a transfer, provide exact pickup and drop addresses. Your cab booking enquiry should identify waiting, parking and overnight requirements so their arrangements and charges can be confirmed.",
  "highlights": [
    "Prebooked Agra tour enquiries",
    "Hotel and station transfer planning",
    "Agra and Gwalior multi-stop journeys",
    "Family and group vehicle bookings"
  ],
  "serviceTypes": [
    "Prebooked pickup & drop",
    "Outstation",
    "Group travel",
    "Itinerary planning"
  ],
  "vehicles": [
    "Sedan",
    "SUV / MPV",
    "Tempo Traveller"
  ],
  "pickupInfo": "Share your Agra hotel, address or station pickup details and the start and finish times required for the vehicle.",
  "dispatch": "We are based in Satna. Please share your route and date in advance so we can confirm vehicle availability, pickup arrangements and the complete quotation.",
  "connections": [
    {
      "label": "Gwalior",
      "to": "/taxi-service-in-gwalior"
    },
    {
      "label": "Lucknow",
      "to": "/taxi-service-in-lucknow"
    },
    {
      "label": "Satna (base)",
      "to": "/"
    }
  ],
  "faqs": [
    {
      "q": "Can I request a vehicle for an Agra sightseeing day?",
      "a": "Yes. Send the date, pickup point, intended stops and finish time. We will confirm availability and quote the vehicle, waiting and applicable charges."
    },
    {
      "q": "Which vehicles can I request for Agra?",
      "a": "Dzire, Etios, Brezza, Ertiga, Bolero, Bolero Neo, Scorpio, Cruiser, Innova Crysta and Kia Carens can be requested. Published Traveller options are 13 (12+1), 17 (16+1), 20 (19+1), 21 (20+1) and 26 (25+1) seats including the driver, subject to availability. A 9-seater option requires a separate capacity and fare confirmation."
    },
    {
      "q": "How do I confirm my Agra booking?",
      "a": "Send your route, dates, pickup time, passengers and luggage details. An enquiry alone is not a confirmed booking; we confirm the vehicle, fare and terms directly with you."
    }
  ]
},
];

// Owner-approved service locations. Satna uses the homepage.
export const SERVICE_LOCATION_NAMES = ["Satna", "Rewa", "Panna", "Khajuraho", "Chhatarpur", "Karwi", "Chitrakoot", "Chitrakoot Dham", "Manikpur", "Mauganj", "Maihar", "Katni", "Umaria", "Bandhavgarh", "Jabalpur", "Shahdol", "Sagar", "Damoh", "Indore", "Bhopal", "Gwalior", "Prayagraj", "Varanasi", "Lucknow", "Kanpur", "Agra"];
export const LOCATIONS = SERVICE_LOCATION_NAMES.filter(city => city !== "Satna").map(city => {
  const location = LOCATION_DATA.find(item => item.city === city);
  if (!location) throw new Error("Missing approved location: " + city);
  return { ...location, ...buildLocationServiceSeo(location) };
});

export const SATNA_CARD = {
  city: "Satna",
  displayName: "Satna",
  slug: "/",
  priority: "primary",
  note: "Our base & primary service area",
};

export const PRIMARY_LOCATIONS = LOCATIONS.filter((l) => l.priority === "primary");
export const SECONDARY_LOCATIONS = LOCATIONS.filter((l) => l.priority === "secondary");

export function getLocationBySlug(slug) {
  const clean = "/" + slug.replace(/^\/hi(?=\/|$)/, "").replace(/^\/+|\/+$/g, "");
  return LOCATIONS.find((l) => l.slug === clean);
}
